import type { Terminal } from 'lucide-react';
import { CloudConversationStartup } from '@/components/cloud-conversation-startup';
import { PanelEmpty } from '@/components/desktop-panel-primitives';
import type { CloudRuntime } from '@/lib/cloud-runtime/cloud-runtime';
import type { CloudConversationStartupPhase } from '@/lib/conversations/cloud-conversation-startup';
import { isCloudRepositoryWorkspace } from '@/lib/sidebar-threads';

/**
 * Placeholder shown when a workspace tool is opened without a ready workspace:
 * either a cloud-workspace startup progress card (while one is spinning up) or a
 * `PanelEmpty` explaining that the tool needs Crowdex Desktop / an open project,
 * or surfacing a cloud provisioning error.
 */
export function WorkspaceUnavailable({
  cloudWorkspaceStarting,
  cloudRuntime,
  cloudClientReady,
  cloudWorkspacePhase,
  cloudWorkspaceError,
  workspace,
  icon,
}: {
  cloudWorkspaceStarting: boolean;
  cloudRuntime: CloudRuntime | null;
  cloudClientReady: boolean;
  cloudWorkspacePhase: CloudConversationStartupPhase | null;
  cloudWorkspaceError: string | null;
  workspace: string | null;
  icon: typeof Terminal;
}) {
  if (cloudWorkspaceStarting || (cloudRuntime !== null && !cloudClientReady)) {
    return (
      <div className="flex min-h-72 items-center px-5 sm:px-7">
        <CloudConversationStartup
          hasRepository={isCloudRepositoryWorkspace(workspace)}
          phase={
            cloudWorkspaceStarting ? 'task-configuration' : (cloudWorkspacePhase ?? 'connecting')
          }
          title="Starting cloud workspace"
        />
      </div>
    );
  }
  return (
    <PanelEmpty
      icon={icon}
      title={
        cloudWorkspaceError !== null
          ? 'Cloud workspace unavailable'
          : 'Available in Crowdex Desktop'
      }
      text={
        cloudWorkspaceError !== null
          ? cloudWorkspaceError
          : workspace === null
            ? 'Open a project to use this tool.'
            : 'Native workspace tools are not available in the web preview.'
      }
    />
  );
}
